import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import SchemaOrg from "@/components/base/SchemaOrg";
import { getArticleById, getHotArticles } from "@/mocks/articles";
import { getCategoryBySlug } from "@/mocks/categories";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || "");
  const relatedArticles = getHotArticles(3).filter((a) => a.id !== id);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
              <i className="ri-file-search-line text-3xl text-secondary-500"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">您访问的文章不存在或已被移除</p>
            <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const category = getCategoryBySlug(article.categorySlug);
  const categoryLabel = category?.name || article.category;

  const breadcrumbItems = [
    { label: categoryLabel, path: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  const breadcrumbSchema = {
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://www.haijiao-resource.com/" },
      { "@type": "ListItem", position: 2, name: categoryLabel, item: `https://www.haijiao-resource.com/category/${article.categorySlug}` },
      { "@type": "ListItem", position: 3, name: article.title },
    ],
  };

  const articleSchema = {
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: { "@type": "Person", name: article.author },
    datePublished: article.date,
    publisher: {
      "@type": "Organization",
      name: "海角千万资源原创",
    },
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema as Record<string, unknown>} />
      <SchemaOrg type="Article" data={articleSchema as Record<string, unknown>} />
      <SchemaOrg type="WebPage" data={{ name: `${article.title} - 海角千万资源原创`, url: `https://www.haijiao-resource.com/article/${id}` }} />

      <Navbar />

      <main className="pt-16">
        {/* Article Header */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <div className="flex items-center gap-3 mb-5">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium hover:bg-primary-200 transition-colors duration-200 whitespace-nowrap"
              >
                {categoryLabel}
              </Link>
              <span className="text-foreground-400 text-xs">{article.readTime} 阅读</span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-6">
              {article.excerpt}
            </p>
            <div className="flex flex-wrap items-center gap-4 text-foreground-500 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-3-line text-primary-500 text-sm"></i>
                </div>
                <span className="font-medium text-foreground-800">{article.author}</span>
              </div>
              <span className="text-foreground-300">|</span>
              <span><i className="ri-calendar-line mr-1"></i>{article.date}</span>
              <span className="text-foreground-300">|</span>
              <span><i className="ri-eye-line mr-1"></i>{article.views.toLocaleString()} 阅读</span>
            </div>
          </div>
        </section>

        {/* Article Cover */}
        <section className="px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="w-full h-64 md:h-96 rounded-2xl overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <article
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            {article.tags.length > 0 && (
              <div className="mt-12 pt-6 border-t border-background-200">
                <h4 className="text-sm font-medium text-foreground-700 mb-3">文章标签</h4>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 bg-background-100 text-foreground-600 rounded-full text-xs border border-background-200"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 mb-8">
                <i className="ri-links-line text-primary-500"></i>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  相关推荐
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((item) => (
                  <Link
                    key={item.id}
                    to={`/article/${item.id}`}
                    className="group rounded-2xl bg-background-50 overflow-hidden hover:shadow-sm transition-all duration-200 cursor-pointer"
                    onClick={() => window.scrollTo({ top: 0, behavior: "instant" })}
                  >
                    <div className="w-full h-44">
                      <img
                        src={item.coverImage}
                        alt={item.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                        {item.title}
                      </h3>
                      <div className="flex items-center gap-3 text-foreground-500 text-xs mt-3">
                        <span>{item.author}</span>
                        <span>{item.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}